import { useState } from "react";
import { Download, RefreshCw, FileDown } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { DatePicker } from "./DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { toast } from "sonner";
import { downloadFile } from "../lib/download";

const COMPANY_OPTIONS = [
  { value: "all", label: "All Companies" },
  { value: "empact-studio", label: "Empact Studio" },
  { value: "event-resource", label: "Event Resource" },
  { value: "vijay-sabhlok", label: "Vijay Sabhlok" },
];

export default function ExportDialog({ 
  open, 
  onOpenChange, 
  dataType, 
  title,
  showDateFilter = true,
  showCompanyFilter = true 
}) {
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportCompany, setExportCompany] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({
        format: exportFormat,
        company: exportCompany,
      });
      if (showDateFilter && startDate) params.set("start_date", startDate);
      if (showDateFilter && endDate) params.set("end_date", endDate);
      
      const url = `/api/export/${dataType}?${params.toString()}`;
      const filename = `${dataType}_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      
      // Close dialog FIRST so its overlay doesn't block the download
      onOpenChange(false);
      
      const success = await downloadFile(url, filename);
      if (success) {
        toast.success(`${exportFormat.toUpperCase()} downloaded successfully`);
      }
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-brand-orange" />
            Export {title}
          </DialogTitle>
          <DialogDescription>
            Choose format and filters for your export
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup 
              value={exportFormat} 
              onValueChange={setExportFormat}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pdf" id="format-pdf" />
                <Label htmlFor="format-pdf" className="cursor-pointer">PDF</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="csv" id="format-csv" />
                <Label htmlFor="format-csv" className="cursor-pointer">CSV</Label>
              </div>
            </RadioGroup>
          </div>

          {showCompanyFilter && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Company</Label>
              <Select value={exportCompany} onValueChange={setExportCompany}>
                <SelectTrigger data-testid="export-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {showDateFilter && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Date Range (Optional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <DatePicker value={startDate} onChange={setStartDate} />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <DatePicker value={endDate} onChange={setEndDate} />
                </div>
              </div>
              <p className="text-xs text-slate-400">Leave empty to export all records</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-brand-orange hover:bg-[#e09515] text-brand-navy gap-2"
            data-testid="export-confirm-btn"
          >
            {exporting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Exporting...</>
            ) : (
              <><Download className="w-4 h-4" /> Export</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
